import { Injectable, inject } from '@angular/core';
import { HisBaseGlobalsService } from '../../service/base/his-base-globals.service';

/**
 * Service zur Ersetzung von i18n-Schlüsseln durch den Ausdruck in der aktuellen Sprache.
 *
 * Dieser Service sollte überall da verwendet werden, wo es kein HTML gibt und deshalb die ui-pipe
 * nicht anwendbar ist, zum Beispiel bei Notifications.
 */
@Injectable({providedIn: 'root'})
export class HisUiService {
    private hisBaseGlobalsService = inject(HisBaseGlobalsService);


    /**
     * Ersetzung von i18n-Keys mit Parametern.
     * Parameter, die selbst i18n-Schlüssel sind, müssen vor Aufruf dieser Methode ersetzt worden sein!
     *
     * @param key der zu ersetzende i18n-Key
     * @param params Array mit weiteren Parametern welche in die Placeholder gesetzt werden sollen.
     * Die varargs-Definition erlaubt, einen oder mehrere Parameter ohne Array-Definition zu übergeben.
     */
    translate( key: string, ...params: ( string | number | Date )[] ): string {
        return this.doTranslate( key, false, ...params );
    }
	
    /**
	 * Übersetzt den gegebenen i18n-Schlüssel oder gibt einen leeren String zurück, falls keine Übersetzung existiert.
	 *
	 * @param key Der i18n-Schlüssel.
	 * @param params Optionale Parameter für Platzhalter in der Übersetzung.
	 * @returns Die übersetzte Zeichenkette oder ein leerer String, falls keine Übersetzung gefunden wurde.
	 */
    translateOrEmpty( key: string, ...params: ( string | number | Date )[] ): string {
        return this.doTranslate( key, true, ...params );
    }

    private doTranslate( key: string, returnEmptyIfNotFound: boolean, ...params: ( string | number | Date )[] ): string {
        if ( this.hisBaseGlobalsService.system.editUiTexts ) {
            let label = this.hisBaseGlobalsService.getUi( key );
            if ( !label || label === 'EMPTY' ) {
                let translatableKey = key + '||' + key;
                return translatableKey !== '||' ? translatableKey : '';
            }
            // Placeholder-Ersetzung wie im Normalfall
            label = this.replacePlaceholders( label, params );
            let escapedKey = key.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            let escapedLabel = label.replace( /&/g, '&amp;' ).replace( /"/g, '&quot;' ).replace( /</g, '&lt;' ).replace( />/g, '&gt;' );
            let translatableString = escapedKey + '||' + escapedLabel;
            return translatableString !== '||' ? translatableString : '';
        }

        // leere Werte bleiben leer
        if ( key == null || key.length === 0 ) {
            return key;
        }

        let label = this.hisBaseGlobalsService.getUi( key );
        let hisdev = this.hisBaseGlobalsService.system.hisDevelopmentMode;
        if ( !label ) {
            if ( returnEmptyIfNotFound ) {
                return '';
            }
            if ( hisdev === true ) {
                label = '???' + key + '???';
            } else {
                label = key;
            }
        }

        if ( label === 'EMPTY' ) {
            label = '';
        }

        label = this.replacePlaceholders( label, params );

        return label;
    }
    // the following replacement algorithm is capable to replace parameters in any order and
    // the same parameter several times
    private replacePlaceholders( label: string, params: ( string | number | Date )[] ): string {
        let paramCount = params ? params.length : 0;
        let pos2 = -1;
        while ( true ) {
            if ( label == null || label.trim() === '' ) {
                break; // done if label is empty
            }
            let pos = label.indexOf( '{', pos2 + 1 );
            if ( pos < 0 ) {
                break; // done
            }
            pos2 = label.indexOf( '}', pos + 1 );
            if ( pos2 < 0 ) {
                console.log( 'Error:', label, 'contains unmatched opening parameter bracket \'{\'' );
                break;
            }
            let paramStr = label.substring( pos + 1, pos2 );
            let placeHolder = '{' + paramStr + '}';
            let paramIndex = parseInt( paramStr, 10 );
            if ( paramIndex >= paramCount ) {
                console.log( 'Error:', label, 'contains', placeHolder, ', but only', paramCount, 'parameters have been passed.' );
                continue; // omit this param, but continue with others
            }

            // Param je nach Datentyp in string konvertieren
            let substring = '';
            let param: string | number | Date = params[paramIndex];
            if ( param !== undefined && param !== null ) {
                let paramTypeStr = Object.prototype.toString.call( param );
                switch ( paramTypeStr ) {
                    case '[object Date]': substring = this.formatDate( param as Date ); break;
                    case '[object Number]': substring = '' + ( param as number ); break;
                    case '[object String]': substring = param as string; break;
                    default: console.log( 'ERROR: Unexpected param type:', paramTypeStr );
                }
            }

            // Ersetzung des Placeholders
            label = label.replace( placeHolder, substring );

            // #348309 erst nach dem ersetzten String weitersuchen, damit es keine Rekursion geben kann,
            // wenn ein Parameter selbst vermeintliche Placeholder enthält
            pos2 += substring.length - placeHolder.length;
        }

        return label;
    }

    translateCommaSeparated( keys: string[] ): string {
        let combinedLabel: string = null;
        keys.forEach( key => {
            combinedLabel = combinedLabel == null ?
                this.translate( key ) :
                combinedLabel + ( key === keys[keys.length - 1] ? ' ' + this.translate( 'de.his.javang.und' ) : ',' ) + ' '
                + this.translate( key );
        } );
        return combinedLabel;
    }

    private formatDate( param: Date ): string {
        // Vorläufiges Format wie ein Datum angezeigt wird: dd.mm.yyyy
        let formatedDate = param.getDate() + '.' + ( param.getMonth() + 1 ) + '.' + param.getFullYear();
        return formatedDate;
    }
}
